import React from 'react';
import { ArrowUpRight, Sparkles, Sparkle, ShieldCheck, Palette, Zap } from 'lucide-react';
import Reveal from './Reveal';

const IMG_URL =
  'https://customer-assets.emergentagent.com/job_solar-minimal-clean/artifacts/ymqr15d7_IMG_6818.png';

const FEATURES = [
  {
    icon: ShieldCheck,
    title: 'Igienici e resistenti',
    desc: 'Facili da pulire e impossibili da ignorare. Resistono al ritmo di un locale vero.',
  },
  {
    icon: Palette,
    title: 'Stile su misura',
    desc: "Progettati per abbinarsi all'arredamento del tuo locale. Forme, materiali e colori scelti con te.",
  },
  {
    icon: Zap,
    title: 'Accesso rapido',
    desc: 'I tuoi clienti visualizzano il menù digitale in un secondo, migliorando l\'esperienza e velocizzando gli ordini.',
  },
];

export default function ThreeD() {
  return (
    <section
      id="menu3d"
      className="relative py-24 md:py-32 bg-[#0f1620] text-white overflow-hidden"
    >
      {/* Background glows */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute -top-32 -left-20 w-[500px] h-[500px] rounded-full bg-[#1e6cb0]/20 blur-3xl" />
        <div className="absolute -bottom-32 -right-10 w-[500px] h-[500px] rounded-full bg-[#5eb0e8]/10 blur-3xl" />
      </div>

      {/* Subtle grid */}
      <div
        className="absolute inset-0 opacity-[0.05] pointer-events-none"
        style={{
          backgroundImage:
            'linear-gradient(#5eb0e8 1px, transparent 1px), linear-gradient(90deg, #5eb0e8 1px, transparent 1px)',
          backgroundSize: '60px 60px',
        }}
      />

      <div className="relative max-w-7xl mx-auto px-5 md:px-8">
        <Reveal variant="fade">
          <div className="flex items-start gap-3 mb-10">
            <span className="text-[#5eb0e8] font-bold text-[14px] tracking-[0.18em]">03</span>
            <span className="w-10 h-px mt-3 bg-[#5eb0e8]/60" />
            <span className="text-[12px] tracking-[0.2em] uppercase font-semibold text-white/60">
              3D &amp; Digital
            </span>
          </div>
        </Reveal>

        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Left: text */}
          <div className="lg:col-span-6">
            <Reveal variant="left" delay={50}>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 border border-white/15 mb-6">
                <Sparkles className="w-3.5 h-3.5 text-[#5eb0e8]" />
                <span className="text-[11px] tracking-[0.18em] uppercase font-bold text-white/85">
                  Novit&agrave; per ristoranti e locali
                </span>
              </div>
            </Reveal>

            <Reveal as="h2" variant="up" delay={150} className="heading-mega text-[40px] sm:text-[54px] md:text-[64px] text-white mb-6">
              Il men&ugrave; del futuro:<br />
              <span className="text-[#5eb0e8]">3D &amp; Digital.</span>
            </Reveal>

            <Reveal as="p" variant="up" delay={280} className="text-[16px] md:text-[17px] leading-relaxed text-white/75 mb-8 max-w-xl">
              Basta fogli plastificati o adesivi rovinati. Realizziamo <span className="font-semibold text-white">espositori 3D di design</span> con
              QR Code integrato per i tuoi tavoli. Eleganza, igiene e digitale in un unico oggetto.
            </Reveal>

            <ul className="space-y-4 mb-9">
              {FEATURES.map((f, i) => (
                <Reveal key={f.title} variant="left" delay={350 + i * 110}>
                  <li className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white/10 border border-white/15 flex items-center justify-center flex-shrink-0">
                      <f.icon className="w-4 h-4 text-[#5eb0e8]" />
                    </div>
                    <div>
                      <div className="font-display text-[16px] font-bold text-white mb-1">
                        {f.title}
                      </div>
                      <p className="text-[14.5px] leading-relaxed text-white/70">{f.desc}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ul>

            <Reveal variant="up" delay={700}>
              <div className="p-5 rounded-2xl bg-gradient-to-br from-[#1e6cb0]/30 to-[#5eb0e8]/10 border border-[#5eb0e8]/30 mb-8">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkle className="w-4 h-4 text-[#5eb0e8]" />
                  <span className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#5eb0e8]">
                    In una frase
                  </span>
                </div>
                <p className="font-display text-[18px] md:text-[20px] font-bold text-white leading-snug">
                  Trasforma i tuoi tavoli in uno strumento di marketing.
                </p>
              </div>
            </Reveal>

            <Reveal variant="up" delay={820}>
              <a
                href="https://wa.me/393383463712?text=Ciao!%20Mi%20interessano%20gli%20espositori%203D%20con%20QR%20per%20il%20men%C3%B9.%20Possiamo%20parlarne?"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-[#1e6cb0] hover:bg-[#2479c4] text-white px-7 py-4 rounded-full font-semibold transition-colors shadow-[0_14px_40px_-10px_rgba(30,108,176,0.55)]"
              >
                Richiedi un preventivo 3D <ArrowUpRight className="w-4 h-4" />
              </a>
            </Reveal>
          </div>

          {/* Right: image showcase */}
          <Reveal variant="right" delay={200} className="lg:col-span-6">
            <div className="relative">
              <div className="absolute -inset-4 rounded-[28px] bg-gradient-to-br from-[#5eb0e8]/30 via-transparent to-[#1e6cb0]/40 blur-xl opacity-70" />
              <div className="relative rounded-3xl overflow-hidden bg-white border border-white/10 shadow-[0_30px_80px_-25px_rgba(0,0,0,0.6)]">
                <div className="aspect-square w-full">
                  <img
                    src={IMG_URL}
                    alt="Espositori 3D con QR Code"
                    className="w-full h-full object-cover"
                  />
                </div>
                {/* Corner badges */}
                <div className="absolute top-4 left-4 px-3 py-1.5 rounded-full bg-white/95 backdrop-blur text-[10px] tracking-[0.18em] uppercase font-bold text-[#1e6cb0] shadow-md">
                  Stampa 3D Premium
                </div>
                <div className="absolute bottom-4 right-4 px-3 py-1.5 rounded-full bg-[#1e6cb0] text-[10px] tracking-[0.18em] uppercase font-bold text-white shadow-md">
                  QR integrato
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
